// Click-to-load YouTube embeds. The facade is plain HTML (a thumbnail button),
// so it prerenders and works inside Markdown bodies; one delegated click
// handler (installYoutubeFacades, called from the root route) swaps the button
// for a youtube-nocookie player that starts playing straight away.

const ID = '[\\w-]{11}'
const EMBED_SRC = new RegExp(
  `^(?:https?:)?//(?:www\\.)?youtube(?:-nocookie)?\\.com/embed/(${ID})(?:\\?(.*))?$`,
  'i',
)

const escapeAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

/** Parses a YouTube embed URL (`/embed/<id>?...`) into its id and query. */
export function parseYoutubeEmbed(src?: string) {
  const m = src && EMBED_SRC.exec(src.replace(/&amp;/g, '&'))
  return m ? { id: m[1], query: m[2] ?? '' } : null
}

const WATCH_URL = new RegExp(
  `^https?://(?:(?:www\\.|m\\.)?youtube\\.com/(?:watch\\?(?:.*&)?v=|shorts/|embed/)|youtu\\.be/)(${ID})(?:[?&#].*)?$`,
  'i',
)

/** Parses a YouTube page URL (watch, youtu.be, shorts, embed) into its id. */
export function parseYoutubeUrl(url?: string) {
  const m = url && WATCH_URL.exec(url.replace(/&amp;/g, '&'))
  return m ? { id: m[1] } : null
}

/** Facade markup: a button showing the video thumbnail and a play icon. */
export function youtubeFacadeHtml(id: string, title: string, query = '') {
  const label = escapeAttr(`Play video: ${title}`)
  return (
    `<button type="button" class="yt-facade" data-youtube-id="${id}"` +
    ` data-youtube-title="${escapeAttr(title)}"` +
    ` data-youtube-query="${escapeAttr(query)}" aria-label="${label}">` +
    `<img src="https://i.ytimg.com/vi/${id}/hqdefault.jpg" alt="" loading="lazy" decoding="async" width="480" height="360" />` +
    `<span class="yt-facade-play" aria-hidden="true"></span></button>`
  )
}

/** Replaces YouTube iframes in an HTML string with click-to-load facades. */
export function facadeYoutubeIframes(html: string): string {
  return html.replace(/<iframe\b[^>]*>\s*<\/iframe>/gi, (tag) => {
    const src = /\ssrc=(["'])(.*?)\1/i.exec(tag)?.[2]
    const yt = parseYoutubeEmbed(src)
    if (!yt) return tag
    const title = /\stitle=(["'])(.*?)\1/i.exec(tag)?.[2] || 'YouTube video'
    return youtubeFacadeHtml(yt.id, title.replace(/&amp;/g, '&'), yt.query)
  })
}

let youtubeApi: Promise<YTNamespace> | undefined

/** Loads the YouTube IFrame API once (~1 KB loader + player script). */
function loadYoutubeApi(): Promise<YTNamespace> {
  return (youtubeApi ??= new Promise<YTNamespace>((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previous?.()
      if (window.YT) resolve(window.YT)
      else reject(new Error('YouTube API loaded without window.YT'))
    }
    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.onerror = reject
    document.head.appendChild(script)
    setTimeout(() => reject(new Error('YouTube API timed out')), 5000)
  }).catch((error: unknown) => {
    youtubeApi = undefined
    throw error
  }))
}

const IFRAME_ALLOW = 'autoplay; fullscreen; encrypted-media; picture-in-picture'

function styleFrame(iframe: HTMLIFrameElement, title: string) {
  iframe.title = title
  iframe.allow = IFRAME_ALLOW
  iframe.allowFullscreen = true
  iframe.className = 'yt-facade-frame'
}

/** Plain iframe; with autoplay=1 it's fine in Chrome, but Safari needs a 2nd tap. */
function plainIframe(
  id: string,
  title: string,
  params: URLSearchParams,
  autoplay = true,
) {
  if (autoplay) params.set('autoplay', '1')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.youtube-nocookie.com/embed/${id}?${params}`
  styleFrame(iframe, title)
  return iframe
}

/** iPhone/iPad, including iPadOS which reports itself as a Mac. */
const isIos = () =>
  /iP(?:hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

function activate(button: HTMLElement) {
  const { youtubeId, youtubeTitle, youtubeQuery } = button.dataset
  if (!youtubeId || button.getAttribute('aria-busy')) return
  const title = youtubeTitle || 'YouTube video'
  const params = new URLSearchParams(youtubeQuery)
  button.setAttribute('aria-busy', 'true')
  // iOS only allows autoplay with sound inside the tap's own gesture, and that
  // is lost while the iframe (or the IFrame API) loads asynchronously, so even
  // playVideo() needed a second tap. Muted inline autoplay is always allowed,
  // so start muted on the first tap; the viewer unmutes in YouTube's controls.
  if (isIos()) {
    params.set('mute', '1')
    params.set('playsinline', '1')
    const iframe = plainIframe(youtubeId, title, params)
    button.replaceWith(iframe)
    iframe.focus()
    return
  }
  // Safari (and iOS) ignore autoplay=1 in an iframe inserted after a click, so
  // the video needed a second tap. Calling playVideo() through the IFrame API
  // once the player is ready starts it on the first tap (as lite-youtube does).
  loadYoutubeApi().then(
    (YT) => {
      const target = document.createElement('div')
      button.replaceWith(target)
      const player = new YT.Player(target, {
        host: 'https://www.youtube-nocookie.com',
        videoId: youtubeId,
        playerVars: {
          ...Object.fromEntries(params),
          autoplay: '1',
          playsinline: '1',
        },
        events: { onReady: (e) => e.target.playVideo() },
      })
      const iframe = player.getIframe()
      styleFrame(iframe, title)
      iframe.focus()
    },
    () => {
      const iframe = plainIframe(youtubeId, title, params)
      button.replaceWith(iframe)
      iframe.focus()
    },
  )
}

/**
 * Mouse users: swap in the real (not autoplaying) player as soon as the
 * pointer is over the facade, so the click lands on YouTube's own play button.
 * Desktop Safari won't start sound from a cross-origin iframe on our behalf,
 * even via playVideo(), so this is the only way to get one-click playback
 * there. Touch and keyboard users still go through activate().
 */
function warm(button: HTMLElement) {
  const { youtubeId, youtubeTitle, youtubeQuery } = button.dataset
  if (!youtubeId || button.getAttribute('aria-busy')) return
  const iframe = plainIframe(
    youtubeId,
    youtubeTitle || 'YouTube video',
    new URLSearchParams(youtubeQuery),
    false,
  )
  button.replaceWith(iframe)
}

const facadeOf = (e: Event) =>
  e.target instanceof Element
    ? e.target.closest<HTMLElement>('.yt-facade')
    : null

/** Installs the delegated handlers; returns a cleanup function. */
export function installYoutubeFacades() {
  const onClick = (e: MouseEvent) => {
    const button = facadeOf(e)
    if (button) activate(button)
  }
  // Short dwell so scrolling past videos with the mouse doesn't load them all.
  let timer = 0
  const onPointerOver = (e: PointerEvent) => {
    clearTimeout(timer)
    const button = facadeOf(e)
    if (button && e.pointerType === 'mouse')
      timer = window.setTimeout(() => warm(button), 150)
  }
  document.addEventListener('click', onClick)
  document.addEventListener('pointerover', onPointerOver)
  return () => {
    clearTimeout(timer)
    document.removeEventListener('click', onClick)
    document.removeEventListener('pointerover', onPointerOver)
  }
}
